/**
 * Main V5 Application Component
 * Uses React Router for navigation between views
 */

import React, { useRef } from 'react';
import { Routes, Route, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import axios from 'axios';
import { SimulationDirectory } from './components/SimulationDirectory';
import { V5ChatDashboard } from './components/V5ChatDashboard';
import { CandidatesPage } from './components/CandidatesPage';
import { ResultsPage } from './components/ResultsPage';
import { CandidateComparisonPage } from './components/CandidateComparisonPage';
import { EvaluationPage } from './components/EvaluationPage';
import { SettingsPage } from './components/SettingsPage';
import { InvitationAcceptPage } from './components/InvitationAcceptPage';
import { CustomSimulationBuilder } from '../v4/components/CustomSimulationBuilder';
import { StudioPlusBuilder } from '../v4/components/StudioPlusBuilder';
import { API_BASE, getAuthHeaders } from '../utils/api';
import theme from '../theme';

interface SimulationBlueprint {
    simulation_id: string;
    metadata: {
        name: string;
        theme: string;
        primary_objective: string;
        expected_duration_steps: number;
        difficulty_level: string;
        intended_agents: string[];
        evaluation_focus: string[];
        simulation_run_duration_min?: number;
    };
    environment: any;
    story_skeleton: any;
    agents: any[];
    simulation_flow: any;
    evaluation_guidance: any;
    optional_metadata: any;
}

function SimulationDirectoryRoute() {
    const navigate = useNavigate();

    const handleSimulationSelect = (sessionId: string) => {
        navigate(`/app/simulation/${sessionId}`);
    };

    const handleEditSimulation = (simulationId: string, isReadOnly: boolean) => {
        navigate(`/app/studio?edit=${simulationId}&readonly=${isReadOnly}`);
    };

    const handleDeleteSimulation = async (simulationId: string): Promise<boolean> => {
        try {
            const authHeaders = await getAuthHeaders();
            await axios.delete(`${API_BASE}/custom-simulations/${simulationId}`, { headers: authHeaders });
            return true;
        } catch (error) {
            console.error('Failed to delete simulation:', error);
            return false;
        }
    };

    return (
        <SimulationDirectory
            onSimulationSelect={handleSimulationSelect}
            onEditSimulation={handleEditSimulation}
            onDeleteSimulation={handleDeleteSimulation}
        />
    );
}

function SimulationRoute() {
    const { sessionId } = useParams<{ sessionId: string }>();
    const navigate = useNavigate();

    const handleBack = () => {
        navigate('/app');
    };

    if (!sessionId) {
        navigate('/app');
        return null;
    }

    return (
        <V5ChatDashboard
            sessionId={sessionId}
            onBack={handleBack}
        />
    );
}

function CandidatesRoute() {
    return <CandidatesPage />;
}

function ResultsRoute() {
    return <ResultsPage />;
}

function ComparisonRoute() {
    const [searchParams] = useSearchParams();
    const idsParam = searchParams.get('ids');
    const candidateIds = idsParam ? idsParam.split(',').filter(Boolean) : [];

    return (
        <CandidateComparisonPage
            candidateIds={candidateIds}
        />
    );
}

function EvaluationRoute() {
    const { candidateId } = useParams<{ candidateId: string }>();
    const navigate = useNavigate();

    const handleBack = () => {
        navigate('/app/results');
    };

    if (!candidateId) {
        navigate('/app/results');
        return null;
    }

    return (
        <EvaluationPage
            candidateId={candidateId}
            onBack={handleBack}
        />
    );
}

function StudioRoute() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const abortPollingRef = useRef(false);

    const editSimulationId = searchParams.get('edit') || undefined;
    const isReadOnly = searchParams.get('readonly') === 'true';
    const isEditMode = !!editSimulationId;

    const pollInitializationStatus = async (sessionId: string, maxAttempts = 150): Promise<boolean> => {
        const authHeaders = await getAuthHeaders();
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            if (abortPollingRef.current) {
                console.log('Polling aborted');
                return false;
            }

            try {
                const statusResponse = await axios.get(
                    `${API_BASE}/sessions/${sessionId}/initialization/status`,
                    { headers: authHeaders }
                );
                const status = statusResponse.data.data.status;

                if (status === 'ready') {
                    return true;
                }

                await new Promise(resolve => setTimeout(resolve, 1000));
            } catch (err) {
                console.warn(`Poll attempt ${attempt + 1} failed:`, err);
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
        return false;
    };

    const handleLaunchCustomSimulation = async (blueprint: SimulationBlueprint): Promise<string> => {
        abortPollingRef.current = false;
        const authHeaders = await getAuthHeaders();

        const blueprintJson = JSON.stringify(blueprint, null, 2);
        const blob = new Blob([blueprintJson], { type: 'application/json' });
        const formData = new FormData();
        formData.append('blueprint', blob, `${blueprint.simulation_id}.json`);

        const response = await axios.post(`${API_BASE}/initialize`, formData, {
            headers: {
                ...authHeaders,
                'Content-Type': 'multipart/form-data'
            }
        });

        const sessionId = response.data.data.simulation_state.session_id;
        console.log('Custom simulation initialized with session:', sessionId);

        const isReady = await pollInitializationStatus(sessionId);

        if (!isReady) {
            throw new Error('Simulation initialization timed out. Please try again.');
        }

        return sessionId;
    };

    const handleCancel = () => {
        abortPollingRef.current = true;
        navigate('/app');
    };

    const handleSimulationReady = (sessionId: string) => {
        console.log('Custom simulation ready:', sessionId);
        navigate(`/app/simulation/${sessionId}`);
    };

    const handleSaveComplete = () => {
        navigate('/app');
    };

    return (
        <ChakraProvider theme={theme}>
            <CustomSimulationBuilder
                onLaunch={handleLaunchCustomSimulation}
                onCancel={handleCancel}
                onSimulationReady={handleSimulationReady}
                editSimulationId={editSimulationId}
                isEditMode={isEditMode}
                isReadOnly={isReadOnly}
                onSaveComplete={handleSaveComplete}
            />
        </ChakraProvider>
    );
}

function SettingsRoute() {
    return <SettingsPage />;
}

function InvitationRoute() {
    return <InvitationAcceptPage />;
}

function StudioPlusRoute() {
    return <StudioPlusBuilder />;
}

export function V5Routes() {
    return (
        <Routes>
            <Route index element={<SimulationDirectoryRoute />} />
            <Route path="simulation/:sessionId" element={<SimulationRoute />} />
            <Route path="candidates" element={<CandidatesRoute />} />
            <Route path="results" element={<ResultsRoute />} />
            <Route path="comparison" element={<ComparisonRoute />} />
            <Route path="evaluation/:candidateId" element={<EvaluationRoute />} />
            <Route path="studio" element={<StudioRoute />} />
            <Route path="studio-plus" element={<StudioPlusRoute />} />
            <Route path="settings" element={<SettingsRoute />} />
            <Route path="settings/:section" element={<SettingsRoute />} />
            <Route path="invitation/:token" element={<InvitationRoute />} />
            <Route path="*" element={<SimulationDirectoryRoute />} />
        </Routes>
    );
}

export function V5App() {
    return <V5Routes />;
}

export default V5App;
